import { ColumnDef } from "@tanstack/react-table";

import CheckDuplicateIcon from "src/core/assets/images/icons/check-duplicate.svg?react";
import CheckIcon from "src/core/assets/images/icons/check.svg?react";
import TrashIcon from "src/core/assets/images/icons/delete.svg?react";
import WarningIcon from "src/core/assets/images/icons/warningTransparent.svg?react";
import { DocumentsColumn } from "src/core/components/stakeholders-datatable/DocumentsColumn";
import { RiskColumn } from "src/core/components/stakeholders-datatable/RiskColumn";
import { Button } from "src/core/components/ui/button";
import { MappedStakeholder } from "src/core/constants/folder";
import { StakeholdersCounter } from "src/core/contexts/FolderContext";

type StakeholdersDatatableColumnsProps = {
  t: (key: string) => string;
  setOpenDeleteModal: (open: boolean, row?: MappedStakeholder) => void;
  setOpenEditModal: (open: boolean, row?: MappedStakeholder) => void;
  setOpenEntityDetailsModal: (open: boolean, row?: MappedStakeholder) => void;
  setOpenDuplicateCheckModal: (open: boolean, row?: MappedStakeholder) => void;
  isReadOnly?: boolean;
  handleDocument: (code: string, stakeholder: MappedStakeholder, inputId: string) => void;
  handlePreviewDocument: (document: { id: string; name: string }) => void;
  fileLoading: string[];
  handleExternalDocumentChange: (
    checked: boolean,
    code: string,
    stakeholderId: string,
    documentId: string | null,
    inputId: string
  ) => void;
  stakeholdersCounter: StakeholdersCounter;
  isPPH: boolean;
};

export const stakeholdersDatatableColumns = ({
  t,
  setOpenDeleteModal,
  setOpenEditModal,
  setOpenEntityDetailsModal,
  setOpenDuplicateCheckModal,
  isReadOnly = false,
  handleDocument,
  handlePreviewDocument,
  fileLoading,
  handleExternalDocumentChange,
  stakeholdersCounter,
  isPPH,
}: StakeholdersDatatableColumnsProps): ColumnDef<MappedStakeholder>[] => {
  const baseColumns: ColumnDef<MappedStakeholder>[] = [
    {
      accessorKey: "datas",
      header: () => (
        <div className="flex flex-row items-center justify-between pr-20">
          {t("stakeholders.datatable.columns.data")}
          {stakeholdersCounter.totalStakeholdersWithInfoMissing > 0 && (
            <span className="item-counter-alert-datatable-stakeholders">
              {stakeholdersCounter.totalStakeholdersWithInfoMissing}
            </span>
          )}
        </div>
      ),
      cell: ({ row }) => {
        return (
          <>
            <div
              role="button"
              className="stakeholders-datatable-data-card"
              onClick={() => {
                setOpenEditModal(true, row.original);
              }}
            >
              <div className="flex flex-row items-center justify-between w-full">
                <div className="flex-1 min-w-0 pr-2">
                  <div className="line-clamp-2">
                    {row.original?.isFolderCustomer && !isPPH
                      ? row.original?.socialReason
                      : isPPH &&
                        (row.original?.firstName === t("form.createClient.emptyPPHName") ||
                          row.original?.lastName === t("form.createClient.emptyPPHName"))
                      ? t("form.createClient.emptyPPHName")
                      : row.original?.firstName?.split(",")[0] +
                        " " +
                        row.original?.lastName?.toUpperCase()}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {row.original?.missingRequiredFieldCount === 0 ? (
                    <CheckIcon className="text-blue-completed w-[16px] h-[16px]" />
                  ) : (
                    <WarningIcon className="w-[16px] h-[16px] text-red-medium" />
                  )}
                </div>
              </div>
            </div>
            {!isReadOnly &&
              !row.original?.isFolderCustomer &&
              !row.original?.duplicateCheckDone && (
                <Button
                  type="button"
                  variant="iconTertiary"
                  size="none"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDuplicateCheckModal(true, row.original);
                  }}
                  disabled={
                    !row.original?.duplicateCheckFetched ||
                    row.original?.duplicateCheckLoading ||
                    (row.original?.duplicateCandidates?.length ?? 0) < 1
                  }
                  loading={row.original?.duplicateCheckLoading}
                >
                  <CheckDuplicateIcon className="size-20" />
                  {t("stakeholders.datatable.potentialDuplicate")}
                </Button>
              )}
          </>
        );
      },
      size: 180,
    },
    {
      accessorKey: "documents",
      header: () => (
        <div className="flex flex-row items-center gap-x-[80px] pr-20 pl-5">
          {t("stakeholders.datatable.columns.documents")}
          {stakeholdersCounter.totalDocumentsMissing > 0 && (
            <span className="item-counter-alert-datatable-stakeholders">
              {stakeholdersCounter.totalDocumentsMissing}
            </span>
          )}
        </div>
      ),
      cell: ({ row }) => {
        return (
          <DocumentsColumn
            row={row}
            isReadOnly={isReadOnly}
            fileLoading={fileLoading}
            handleDocument={handleDocument}
            handlePreviewDocument={handlePreviewDocument}
            handleExternalDocumentChange={handleExternalDocumentChange}
          />
        );
      },
    },
    {
      accessorKey: "analyse",
      header: () => (
        <div className="flex flex-row items-center justify-between pr-20 pl-5">
          {t("stakeholders.datatable.columns.analyse")}
          {stakeholdersCounter.alerte > 0 && (
            <span className="item-counter-alert-datatable-stakeholders">
              {stakeholdersCounter.alerte}
            </span>
          )}
          {stakeholdersCounter.alerteNoAnalysis > 0 && (
            <span className="item-counter-alert-datatable-stakeholders">!</span>
          )}
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div className="flex flex-col gap-y-5 px-5">
            <RiskColumn row={row} setOpenEntityDetailsModal={setOpenEntityDetailsModal} />
          </div>
        );
      },
    },
  ];

  // Ajouter la colonne action seulement si pas en mode lecture seule
  if (!isReadOnly && !isPPH) {
    baseColumns.push({
      accessorKey: "action",
      header: "",
      cell: ({ row }) => {
        return (
          <div className="flex flex-row gap-x-[20px] justify-center items-center">
            {!row.original.isFolderCustomer && (
              <Button
                type="button"
                variant="iconDestructive"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDeleteModal(true, row.original);
                }}
                aria-label={t("global.delete")}
              >
                <TrashIcon />
              </Button>
            )}
          </div>
        );
      },
    });
  }

  return baseColumns;
};
